<script setup lang="tsx">
import { capitalize } from '@/utils';

import CheckCircleIcon from '~icons/mdi/check-circle';
import CopyIcon from '~icons/mdi/content-copy';
import EmailIcon from '~icons/mdi/email-outline';
import TwitterIcon from '~icons/ri/twitter-x-fill';
import WhatsAppIcon from '~icons/ri/whatsapp-fill';
import FacebookIcon from '~icons/uil/facebook-f';

import { useEvents } from '~/composables/events/useEvents';
import type { SharingOption } from '.';
import { useToast } from '../ui/toast';
import { CopyToastDescription } from '../CopyToastDescription';

defineOptions({ name: 'SharingContent' });

const emit = defineEmits<{
	(e: 'share-clicked', option: SharingOption): void;
}>();

const { toast } = useToast();
const { trackSharePropertyClicked } = useEvents();

type SharingContentProps = {
	mailSubject: string;
	contentToShare: string;
	showLabels?: boolean;
	variant?: 'default' | 'dropdown';
};

const props = withDefaults(defineProps<SharingContentProps>(), {
	showLabels: false,
	variant: 'default'
});

const modelValue = defineModel<boolean>({ required: true });

const sharingOptions = computed<SharingOption[]>(() => [
	{
		value: `https://api.whatsapp.com/send/?text=${props.contentToShare}`,
		label: 'WhatsApp',
		icon: WhatsAppIcon
	},
	{
		value: `https://www.facebook.com/sharer/sharer.php?u=${props.contentToShare}`,
		label: 'Facebook',
		icon: FacebookIcon
	},
	{
		value: `https://twitter.com/share?url=${props.contentToShare}`,
		label: 'Twitter',
		icon: TwitterIcon
	},
	{
		value: `mailto:?subject=${props.mailSubject}&body=${props.contentToShare}`,
		label: 'E-mail',
		icon: EmailIcon
	}
]);

function handleCopyLink() {
	navigator?.clipboard?.writeText(props.contentToShare);
	modelValue.value = false;
	toast({
		description: <CopyToastDescription />
	});

	trackSharePropertyClicked({
		share_whatsapp: false,
		share_facebook: false,
		copy_link: true,
		share_email: false,
		share_instagram: false,
		share_twitter: false
	});
}
</script>

<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div :data-variant="variant" class="font-matter-sq font-normal">
		<NuxtLink
			v-for="option in sharingOptions"
			:key="option.label"
			:to="option.value"
			external
			rel="nofollow external"
			target="_blank"
			class="share-icon-link"
			:aria-label="`Compartilhe no ${capitalize(option.label)}`"
			@click="emit('share-clicked', option)"
		>
			<Component :is="option.icon" class="fill-primary text-primary" />
			<span v-if="showLabels" class="text-primary">
				{{ option.label }}
			</span>
		</NuxtLink>
		<button class="share-icon-link" @click="handleCopyLink()">
			<CopyIcon class="fill-primary text-primary" />
			<span v-if="showLabels" class="text-primary"> Copiar link </span>
		</button>
		<slot></slot>
	</div>
</template>

<style lang="scss" scoped>
[data-variant='default'] {
	display: flex;
	align-items: center;
	gap: 1rem;

	:deep(.share-icon-link) {
		transition: all 0.15s ease-in-out;
		padding: 12px 16px;
		border: solid 1px var(--black);
		width: 2rem;
		height: 2rem;
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--primary);
		svg {
			width: 1rem;
			height: 1rem;
		}

		&:hover {
			background: var(--black);
			color: var(--white);
		}
	}
}

[data-variant='dropdown'] {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;

	:deep(.share-icon-link) {
		transition: all 0.15s ease-in-out;
		color: var(--darkgray);
		display: flex;
		align-items: center;
		gap: 1rem;
		font-size: 0.75rem;

		svg {
			width: 0.9rem;
			height: 0.9rem;
		}

		&:hover {
			color: var(--black);
		}
	}
}
</style>
